import { create } from 'zustand';

/**
 * Tracks which OTHER collaborators are currently dragging which windows in
 * the shared spatial/spatialCanvas canvas, so Window.tsx can render a
 * "being moved by X" indicator. Populated by
 * sharedSpaceWindowsCRDTService's drag-broadcast receiver — never written to
 * directly by UI code.
 *
 * Session-only: this is presentation state derived from an ephemeral CRDT
 * broadcast, not durable window data, so it lives in its own tiny store
 * rather than spaceStore/spaceSpatialUIStore.
 */

export interface WindowDragger {
  userId: string;
  name: string;
  color: string;
  updatedAt: number;
}

interface WindowDragIndicatorState {
  draggers: Record<string, WindowDragger>;
  setDragger: (windowId: string, dragger: Omit<WindowDragger, 'updatedAt'>) => void;
  clearDragger: (windowId: string) => void;
  clearStale: (maxAgeMs: number) => void;
  reset: () => void;
}

export const useWindowDragIndicatorStore = create<WindowDragIndicatorState>((set) => ({
  draggers: {},

  setDragger: (windowId, dragger) => set((state) => ({
    draggers: { ...state.draggers, [windowId]: { ...dragger, updatedAt: Date.now() } },
  })),

  clearDragger: (windowId) => set((state) => {
    if (!(windowId in state.draggers)) return state;
    const next = { ...state.draggers };
    delete next[windowId];
    return { draggers: next };
  }),

  // Safety net: if a collaborator's tab crashes/loses connection mid-drag,
  // the explicit "drag ended" broadcast never arrives. Called periodically
  // from the drag receiver so a stale indicator can't linger forever.
  clearStale: (maxAgeMs) => set((state) => {
    const now = Date.now();
    let changed = false;
    const next: Record<string, WindowDragger> = {};
    for (const [windowId, dragger] of Object.entries(state.draggers)) {
      if (now - dragger.updatedAt > maxAgeMs) {
        changed = true;
      } else {
        next[windowId] = dragger;
      }
    }
    return changed ? { draggers: next } : state;
  }),

  reset: () => set({ draggers: {} }),
}));
